/**
 * Экран сервиса машины: техническое состояние, переводы цикла ремонта и
 * сервисная история. Это **не карточка машины**: здесь опознание — вид, VIN,
 * реквизит техпаспорта, состояние — и ничего сверх: ни фото, ни раздела
 * неисправностей, ни комментариев.
 *
 * Главный риск блока — цикл ремонта. Экран предлагает **весь порождённый
 * перечень состояний целиком, включая текущее**, и не вычисляет допустимых
 * переходов ни в каком виде: у ответа нет поля с переходами, клиент цикла не
 * знает и знать не может. Нажатие уходит на сервер всегда; отказ приходит
 * статусом 422 и показывается на месте, состояние на экране берётся **из
 * ответа**, а не из нажатого значения.
 *
 * Сервисная запись идемпотентна **заголовком**: ключ рождается в сетевом слое
 * на каждую отправку заново, две одинаковые записи законны, и клиент второй не
 * подавляет. Деньги ходят строкой как пришли: перевода в число и итогов по
 * журналу здесь нет — расход фирмы на ремонт не цена фрахта, но складывать его
 * не просит ни одно требование. Строка заменённых узлов не разбирается и не
 * склеивается: одно поле — одна строка.
 *
 * Журнал только пополняется: автора записи нет намеренно — «кто внёс» придёт
 * слоем аудита, — а операций правки и удаления не существует вовсе.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type {
  RepairStateChangeRequest,
  ServiceRecord,
  ServiceRecordCreateRequest,
  Vehicle,
} from '../api/contract';
import { REPAIR_STATES } from '../api/generated/enums';
import { activeLocale, repairStateStringKey, vehicleKindStringKey } from '../i18n';
import type { Strings } from '../i18n';
import {
  createFieldMessageSlot,
  enumLabel,
  momentLabel,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
  selectEnumOption,
} from './state';
import type { ScreenState } from './state';

export interface VehicleServiceScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly vehicleId: string;
}

/** Введённое человеком — ровно поля тела сервисной записи, и ни одного сверх. */
export interface ServiceRecordInput {
  readonly description: string;
  readonly partsReplaced?: string | null;
  readonly costAmount?: string | null;
  readonly costCurrency?: string | null;
}

export interface VehicleServiceScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  readonly records: readonly ServiceRecord[];
  load(): Promise<void>;
  changeState(chosen: string): Promise<void>;
  addRecord(input: ServiceRecordInput): Promise<void>;
}

export function createVehicleServiceScreen(
  options: VehicleServiceScreenOptions,
): VehicleServiceScreen {
  const { client, strings, companyId, vehicleId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const identity = document.createElement('p');
  const stateChangeForm = document.createElement('form');
  const stateCaption = document.createElement('label');
  const stateCaptionText = document.createElement('span');
  const stateField = document.createElement('select');
  const stateMessage = createFieldMessageSlot();
  const stateSubmit = document.createElement('button');
  const recordsHeading = document.createElement('h2');
  const journalList = document.createElement('ul');
  const journalEmpty = document.createElement('p');
  const recordForm = document.createElement('form');
  const descriptionCaption = document.createElement('label');
  const descriptionCaptionText = document.createElement('span');
  const descriptionField = document.createElement('input');
  const descriptionMessage = createFieldMessageSlot();
  const partsCaption = document.createElement('label');
  const partsCaptionText = document.createElement('span');
  const partsField = document.createElement('input');
  const partsMessage = createFieldMessageSlot();
  const amountCaption = document.createElement('label');
  const amountCaptionText = document.createElement('span');
  const amountField = document.createElement('input');
  const amountMessage = createFieldMessageSlot();
  const currencyCaption = document.createElement('label');
  const currencyCaptionText = document.createElement('span');
  const currencyField = document.createElement('input');
  const currencyMessage = createFieldMessageSlot();
  const recordSubmit = document.createElement('button');
  /** Место сообщения сервера, которому не нашлось поля, — форма members.ts. */
  const notice = createFieldMessageSlot();

  element.className = 'screen screen-vehicle-service';
  identity.className = 'vehicle-identity';
  stateChangeForm.className = 'form';
  stateCaption.className = 'form-field';
  stateCaptionText.className = 'form-field__caption';
  recordForm.className = 'form';
  descriptionCaption.className = 'form-field';
  descriptionCaptionText.className = 'form-field__caption';
  partsCaption.className = 'form-field';
  partsCaptionText.className = 'form-field__caption';
  amountCaption.className = 'form-field';
  amountCaptionText.className = 'form-field__caption';
  currencyCaption.className = 'form-field';
  currencyCaptionText.className = 'form-field__caption';
  stateSubmit.className = 'action';
  recordSubmit.className = 'action';
  journalEmpty.className = 'list-empty';

  heading.textContent = strings.service_title;
  stateCaptionText.textContent = strings.service_state_label;
  stateSubmit.type = 'submit';
  stateSubmit.textContent = strings.service_state_change;
  recordsHeading.textContent = strings.service_records_title;
  journalEmpty.textContent = strings.service_records_empty;
  descriptionCaptionText.textContent = strings.service_description;
  partsCaptionText.textContent = strings.service_parts;
  amountCaptionText.textContent = strings.service_cost;
  currencyCaptionText.textContent = strings.service_currency;
  recordSubmit.type = 'submit';
  recordSubmit.textContent = strings.service_record_add;

  // Имена полей — имена полей тел запросов: по ним сервер называет место
  // своего сообщения. Перечень полей закрыт составом схем — второго денежного
  // поля и раздельных «узлов» и «запчастей» здесь нет и появиться не может.
  stateField.name = 'state';
  descriptionField.name = 'description';
  partsField.name = 'parts_replaced';
  amountField.name = 'cost_amount';
  currencyField.name = 'cost_currency';

  // Набор предлагаемых состояний — порождённый перечень **целиком**, включая
  // текущее: сокращение набора было бы перечнем переходов в клиенте, только
  // записанным вычитанием. Отказ на недопустимый переход приходит от сервера.
  for (const state of REPAIR_STATES) {
    const choice = document.createElement('option');

    choice.value = state;
    choice.textContent = enumLabel(strings, repairStateStringKey(state));
    stateField.append(choice);
  }

  stateCaption.append(stateCaptionText, stateField, stateMessage);
  stateChangeForm.append(stateCaption, stateSubmit);
  descriptionCaption.append(descriptionCaptionText, descriptionField, descriptionMessage);
  partsCaption.append(partsCaptionText, partsField, partsMessage);
  amountCaption.append(amountCaptionText, amountField, amountMessage);
  currencyCaption.append(currencyCaptionText, currencyField, currencyMessage);
  recordForm.append(descriptionCaption, partsCaption, amountCaption, currencyCaption, recordSubmit);

  let state: ScreenState | null = 'loading';
  let vehicle: Vehicle | null = null;
  let records: readonly ServiceRecord[] = [];
  /** Пришёл ли ответ сервера — та же величина, что на экране фирмы. */
  let answered = false;

  function recordRow(record: ServiceRecord): HTMLElement {
    const node = document.createElement('li');

    node.className = 'service-record-row';

    // Одна строка узлов — одной строкой: клиент её не разбирает. Расход —
    // строкой как пришёл: в число деньги не переводятся и не складываются.
    const partsShown = record.parts_replaced ?? '';
    const amountShown = record.cost_amount ?? '';
    const currencyShown = record.cost_currency ?? '';

    node.textContent = [
      momentLabel(activeLocale(), record.created_at),
      record.description,
      partsShown,
      amountShown,
      currencyShown,
    ].join(' ');

    return node;
  }

  function render(): void {
    notice.textContent = '';

    if (state !== null && !answered) {
      // Ответа нет: показывать нечего вовсе, отказ прав и отсутствие объекта
      // дают побайтово один экран — нераскрытие.
      element.replaceChildren(renderScreenState(state, strings));

      return;
    }

    if (vehicle === null) {
      // Список пришёл, а машины с данным адресом в нём нет: содержимого не
      // существует, и это отказ экрана целиком.
      element.replaceChildren(renderScreenState(screenStateFor({ kind: 'failed', code: null }) ?? 'error', strings));

      return;
    }

    // Опознание машины: журнал без него безымянен. Вид и состояние — подписью
    // по строителю, и ничего сверх: фото и неисправности — не этот экран.
    identity.textContent = [
      enumLabel(strings, vehicleKindStringKey(vehicle.kind)),
      vehicle.vin,
      vehicle.tech_passport_number,
      enumLabel(strings, repairStateStringKey(vehicle.repair_state)),
    ].join(' ');

    // Выбранным помечается состояние **из ответа**, а не нажатое значение.
    selectEnumOption(stateField, repairStateStringKey(vehicle.repair_state), repairStateStringKey);

    const rows: HTMLElement[] = [];

    for (const record of records) {
      rows.push(recordRow(record));
    }

    journalList.replaceChildren(...rows);
    // Состояние «пусто» принадлежит **журналу**, а не экрану: пустой журнал —
    // пришедший ответ, и форма записи в нём остаётся.
    element.replaceChildren(
      heading,
      identity,
      notice,
      stateChangeForm,
      recordsHeading,
      records.length === 0 ? journalEmpty : journalList,
      recordForm,
    );
  }

  /**
   * Исход неудачного **действия** — форма member-rights.ts: осмысленный отказ
   * сервера остаётся на экране с содержимым и показывается его словами.
   */
  function actionFailed(form: HTMLElement, error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const asScreen = screenStateFor({
      kind: 'failed',
      code: failure === null ? null : failure.code,
    });

    if (failure === null || failure.code === null || asScreen === 'noAccess') {
      state = asScreen;
      answered = false;
      render();

      return;
    }

    render();

    const homeless = renderFieldMessages(form, failure.details);
    const texts: string[] = [];

    for (const detail of homeless) {
      texts.push(detail.message);
    }

    if (texts.length === 0 && failure.reason !== null) {
      texts.push(failure.reason);
    }

    notice.textContent = texts.join(' ');
  }

  /** Запись реестра с данным адресом — из списка фирмы, второй операции нет. */
  function vehicleIn(fleet: readonly Vehicle[]): Vehicle | null {
    for (const known of fleet) {
      if (known.id === vehicleId) {
        return known;
      }
    }

    return null;
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    vehicle = null;
    records = [];
    answered = false;
    render();

    try {
      const fleet = await client.listCompanyVehicles(companyId);
      const journal = await client.listVehicleServiceRecords(companyId, vehicleId);

      vehicle = vehicleIn(fleet);
      records = journal;
      answered = vehicle !== null;
      state = vehicle === null
        ? screenStateFor({ kind: 'failed', code: null })
        : screenStateFor({ kind: 'done', empty: false });
    } catch (error) {
      vehicle = null;
      records = [];
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function changeState(chosen: string): Promise<void> {
    renderFieldMessages(stateChangeForm, []);

    // Нажатие уходит на сервер всегда: ни один из отказов клиент не
    // предсказывает — повтор текущего состояния тоже решает сервер.
    try {
      const received = await client.changeVehicleRepairState(companyId, vehicleId, {
        state: chosen as RepairStateChangeRequest['state'],
      });

      // Экран перерисовывается по ответу — тот несёт запись целиком, и
      // состояние на экране это состояние сервера, а не нажатое значение.
      vehicle = received;
      state = null;
      render();
    } catch (error) {
      actionFailed(stateChangeForm, error);
    }
  }

  async function addRecord(input: ServiceRecordInput): Promise<void> {
    renderFieldMessages(recordForm, []);

    // Тело — ровно поля ServiceRecordCreateRequest: обязательно только
    // описание, узлы и расход — по факту и строками как введены.
    const body: ServiceRecordCreateRequest = { description: input.description };

    if (typeof input.partsReplaced === 'string' && input.partsReplaced !== '') {
      body.parts_replaced = input.partsReplaced;
    }

    if (typeof input.costAmount === 'string' && input.costAmount !== '') {
      body.cost_amount = input.costAmount;
    }

    if (typeof input.costCurrency === 'string' && input.costCurrency !== '') {
      body.cost_currency = input.costCurrency;
    }

    try {
      const received = await client.addVehicleServiceRecord(companyId, vehicleId, body);

      // Журнал пополняется записью ответа: две одинаковые записи законны, и
      // вторая появляется на экране так же, как первая.
      records = [...records, received];
      state = null;
      render();
    } catch (error) {
      actionFailed(recordForm, error);
    }
  }

  stateChangeForm.addEventListener('submit', (event) => {
    event.preventDefault();

    void changeState(stateField.value);
  });

  recordForm.addEventListener('submit', (event) => {
    event.preventDefault();

    void addRecord({
      description: descriptionField.value,
      partsReplaced: partsField.value === '' ? null : partsField.value,
      costAmount: amountField.value === '' ? null : amountField.value,
      costCurrency: currencyField.value === '' ? null : currencyField.value,
    });
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get records(): readonly ServiceRecord[] {
      return records;
    },

    load,
    changeState,
    addRecord,
  };
}
